import * as React from 'react';
import styles from './outstandingLoan.module.scss';
import { Warning, Info } from 'phosphor-react';
import { ChildWidgetProps } from '..';
import { WidgetService } from '../../../../utils/widgetService';
import { AccordionContainer, SimpleAccordion } from '../../../_common/accordion';
import {
  ILoanDto,
  LoanRangesDto,
  OutstandingLoanBalanceDto,
} from '../../../../models/dtos/loan';
import OutstandingLoanContent from './outstandingLoanContent';
import { BorrowerApi } from '../../../../apis/borrowerApi';
import { IBorrowerDto } from '../../../../models/dtos/borrower';
import { event } from '../../../../utils/tracking';
import { GoogleAnalyticsCategory } from '../../../../models/dtos/enums';
import { LoansApi } from '../../../../apis/loansApi';
import NewLoanSelectorMini from '../eligible/newLoanSelectorMini';
import { LoanStore } from '../../../../stores/loanStore';
import NewLoanSelectorMiniSimple from '../eligible/newLoanSelectorMiniSimple';
import { BorrowerStore } from '../../../../stores/borrowerStore';

export interface OutstandingLoanWidgetProps extends ChildWidgetProps {
  loans: ILoanDto[];
  onViewLoanHistory(): void;
  borrowerApi: BorrowerApi;
  loansApi: LoansApi;
  loanStore: LoanStore;
  onGetLoan(): void;
  borrowerStore: BorrowerStore;
  isUnderCreditLine?: boolean;
  isCreditLineEnabled?: boolean;
  isBelowMinimumMLA?: boolean;
}

export interface OutstandingLoanWidgetState {
  active: boolean;
  borrower: IBorrowerDto;
  outstandingLoanBalance: OutstandingLoanBalanceDto;
  isCreditLine: boolean;
  loanRanges: LoanRangesDto;
  sliderValue: number;
  decrementDisabled: boolean;
  incrementDisabled: boolean;
  errorStatus: number;
  errorMsg: string;
}

class OutstandingLoanWidget extends React.Component<
  OutstandingLoanWidgetProps,
  OutstandingLoanWidgetState
> {
  widgetService = new WidgetService();

  constructor(props: OutstandingLoanWidgetProps) {
    super(props);

    this.props.widgetStore.setMenuVisibility(true);
  }

  interval: NodeJS.Timeout;
  state = {
    active: false,
    borrower: undefined as IBorrowerDto,
    outstandingLoanBalance: undefined as OutstandingLoanBalanceDto,
    isCreditLine: false,
    loanRanges: {
      minimumAmount: 0,
      maximumAmount: 0,
      paymentTerms: [],
    },
    sliderValue: 0,
    decrementDisabled: true,
    incrementDisabled: true,
    errorStatus: 0,
    errorMsg: '',
    isBelowMinimumMLA: false,
  };

  async componentDidMount() {
    const { borrowerStore, loanStore } = this.props;

    try {
      const widgetData = await this.widgetService.fetchOutstandingWidgetData(
        this.props.loansApi
      );

      const isCreditLine =
        (this.props.isCreditLineEnabled || false) &&
        (this.props.isUnderCreditLine || false);

      const loanRanges = isCreditLine
        ? loanStore.loanRanges || {
            minimumAmount: 0,
            maximumAmount: 0,
            paymentTerms: [],
          }
        : {
            minimumAmount: 0,
            maximumAmount: 0,
            paymentTerms: [],
          };

      this.initializeWidgetState(
        borrowerStore?.borrower,
        widgetData.outstandingLoanBalance,
        isCreditLine,
        loanRanges,
        0,
        ''
      );
    } catch (error) {
      this.props.onError(error);
    }
  }

  handleOnViewLoanHistory = () => {
    event({
      category: GoogleAnalyticsCategory.LoanRequestedWidget,
      action: 'Click View my History',
    });
    this.props.onViewLoanHistory();
  };

  handleOnSliderChange = (sliderValue: number) => {
    const decrementDisabled = sliderValue <= this.state.loanRanges.minimumAmount;
    const incrementDisabled = sliderValue >= this.state.loanRanges.maximumAmount;
    this.setState({
      ...this.state,
      sliderValue,
      decrementDisabled,
      incrementDisabled,
    });
  };

  handleOnDecrementClick = () => {
    const sliderValue = this.state.sliderValue - 1000;
    const decrementDisabled = sliderValue <= this.state.loanRanges.minimumAmount;

    this.setState({
      ...this.state,
      sliderValue,
      decrementDisabled,
      incrementDisabled: false,
    });
  };

  handleOnIncrementClick = () => {
    const sliderValue = this.state.sliderValue + 1000;
    const incrementDisabled = sliderValue >= this.state.loanRanges.maximumAmount;

    this.setState({
      ...this.state,
      sliderValue,
      decrementDisabled: false,
      incrementDisabled,
    });
  };

  initializeWidgetState = (
    borrower: IBorrowerDto,
    outstandingLoanBalance: OutstandingLoanBalanceDto | undefined,
    isCreditLine: boolean,
    loanRanges: LoanRangesDto,
    errorStatus: number,
    errorMsg: string
  ) => {
    const maxLoanAmount = loanRanges.maximumAmount;

    this.setState(
      {
        ...this.state,
        borrower,
        outstandingLoanBalance:
          outstandingLoanBalance || this.state.outstandingLoanBalance,
        isCreditLine,
        loanRanges,
        errorStatus,
        errorMsg,
        sliderValue: Math.max(loanRanges.minimumAmount, Math.floor(maxLoanAmount * 0.3)),
      },
      () => {
        const ms = 500 / (maxLoanAmount / 1000);

        this.props.onFinished();

        this.interval = setInterval(() => {
          const nextValue = this.state.sliderValue + 1000;
          if (nextValue <= maxLoanAmount) {
            this.setState({
              ...this.state,
              sliderValue: nextValue,
            });
          } else {
            // Set to exact maximum and stop
            this.setState({
              ...this.state,
              sliderValue: maxLoanAmount,
              decrementDisabled: false,
              incrementDisabled: true,
            });
            clearInterval(this.interval);
          }
        }, ms);
      }
    );
  };

  handleOnSubmit = () => {
    this.props.loanStore.setMaximumLoanAmount(this.state.loanRanges.maximumAmount);
    this.props.loanStore.setLoanAmount(this.state.sliderValue);
    this.props.onGetLoan();
    localStorage.setItem('selectedAmount', this.state.sliderValue.toString());
    localStorage.setItem('loanRanges', JSON.stringify(this.state.loanRanges));

    event({
      category: GoogleAnalyticsCategory.EligibleWidget,
      action: 'Click Get Cash',
      label: this.state.sliderValue.toString(),
    });
  };

  render() {
    const {
      active,
      borrower,
      outstandingLoanBalance,
      loanRanges,
      sliderValue,
      decrementDisabled,
      incrementDisabled,
      isCreditLine,
    } = this.state;
    const { isUnderCreditLine, isBelowMinimumMLA, loanStore } = this.props;
    const maxLoanAmount = loanRanges.maximumAmount;
    const minLoanAmount = loanRanges.minimumAmount;
    const warningIcon = <Warning size={24} weight="fill" />;
    const infoIcon = <Info size={24} weight="fill" />;

    let sliderHidden = true;
    let headerText = 'You have an outstanding balance';
    let bodyText =
      'Whether you need it once or several times, you can apply for a salary advance and get the cash instantly!';
    let subText = '';
    let email = '';
    let icon = infoIcon;
    let iconStyle = styles.iconInformation;

    if (isCreditLine && isUnderCreditLine) {
      headerText = 'You have an outstanding balance';
      bodyText =
        'Whether you need it once or several times, you can apply for a salary advance and get the cash instantly!';
      sliderHidden = false;

      if (isBelowMinimumMLA) {
        headerText = 'You have low credit right now';
        bodyText =
          'You need to have at least PHP 3,000.00 minimum credit to request a cash advance.';
        icon = infoIcon;
        iconStyle = styles.iconError;
        sliderHidden = true;
      } else if (loanStore.loanRangesErrorStatus >= 400) {
        headerText = "Get Cash isn't available right now...";
        bodyText =
          "ReadyCash seems to be down. Don't worry as you can still view your outstanding balance below.";
        subText = 'If you have any concerns, feel free to contact ';
        icon = warningIcon;
        email = ' experts@sprout.ph.';
        iconStyle = styles.iconWarning;
        sliderHidden = true;
      }
    }

    return (
      <div className={styles.widget}>
        <div className={styles.content}>
          <AccordionContainer>
            <SimpleAccordion
              header={headerText}
              headerClassName={styles.title}
              bodyClassName={`${styles.body} ${styles.inner}`}
              iconClassName={iconStyle}
              className={`${styles.container} ${styles.accordion}`}
              active={!active}
              icon={icon}
            >
              <div>
                <p className={`${subText === '' ? 'mb-0' : ''} ${styles.desc}`}>
                  {bodyText}
                </p>
                <p className={`mb-0 ${subText !== '' ? styles.desc : ''}`}>
                  {subText}
                  <span>{email}</span>
                </p>
              </div>
            </SimpleAccordion>
            <div className={!sliderHidden ? styles.bottom : ''}>
              <SimpleAccordion
                header={'OUTSTANDING BALANCE'}
                headerClassName={styles.subTitle}
                bodyClassName={`${styles.body} ${styles.inner}`}
                className={`${styles.container} ${styles.accordion}`}
                active={active}
              >
                <div className="pt-1">
                  <OutstandingLoanContent
                    loan={this.props.loans[0]}
                    borrower={borrower}
                    loanBalance={outstandingLoanBalance}
                    onViewLoanHistory={this.props.onViewLoanHistory}
                  />
                </div>
              </SimpleAccordion>
            </div>
          </AccordionContainer>
        </div>
        <div className={styles.slider} hidden={sliderHidden}>
          {/* logic, if FF CreditLine is on and sufficient balance then display slider and additional logic if max and min is same then display the 
          LoanSelectorMiniSimple else LoanSelectorMini  */}
          {isCreditLine &&
          isUnderCreditLine &&
          !isBelowMinimumMLA &&
          loanStore.loanRangesErrorStatus < 400 &&
          maxLoanAmount === minLoanAmount ? (
            <NewLoanSelectorMiniSimple
              value={sliderValue}
              onSubmit={this.handleOnSubmit}
            />
          ) : (
            <NewLoanSelectorMini
              min={minLoanAmount}
              max={maxLoanAmount}
              value={sliderValue}
              onSliderChange={this.handleOnSliderChange}
              onDecrementClick={this.handleOnDecrementClick}
              onIncrementClick={this.handleOnIncrementClick}
              decrementDisabled={decrementDisabled}
              incrementDisabled={incrementDisabled}
              onSubmit={this.handleOnSubmit}
            />
          )}
        </div>
      </div>
    );
  }
}

export default OutstandingLoanWidget;
